import React from "react";
import DharmaWheel from "../components/DharmaWheel";

/**
 * MISSION / MARKETING POSTER (A3 print, 1240 × 1754 @ 96 DPI)
 *
 * A community outreach poster that answers four questions:
 *   1. Who are we?
 *   2. Why do we exist?
 *   3. What do we do?
 *   4. How can you join in?
 *
 * Distinct from /poster/a3 (which is the "website is live" launch poster).
 * Captured at 1240 × 1754, scaled to 2480 × 3508 for A3 print by
 * scripts/capture_posters.py (deviceScaleFactor 2).
 */
export default function PosterMission() {
  return (
    <div
      style={{
        width: 1240,
        minHeight: 1754,
        margin: "0 auto",
        background: "#0B1F4D",
        color: "#FDFBF7",
        fontFamily: "'Work Sans', system-ui, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="poster-mission"
    >
      {/* Decorative top band */}
      <div style={{ height: 8, background: "linear-gradient(90deg, #CFA052 0%, #E07A5F 50%, #CFA052 100%)" }} />

      {/* Header */}
      <div style={{ padding: "70px 100px 0", display: "flex", alignItems: "center", gap: 32 }}>
        <DharmaWheel size={100} color="#CFA052" ringColor="#001B3A" />
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 48, lineHeight: 1, fontWeight: 600 }}>
            Ambedkarite Buddhist Global Network UK
          </div>
          <div style={{ fontSize: 16, letterSpacing: "0.32em", color: "#CFA052", marginTop: 12, textTransform: "uppercase", fontWeight: 600 }}>
            Rooted in Dhamma · Inspired by Babasaheb
          </div>
        </div>
      </div>
      <div style={{ marginTop: 32, marginLeft: 100, marginRight: 100, height: 1, background: "linear-gradient(90deg, #CFA052 0%, transparent 80%)" }} />

      {/* Mission statement */}
      <div style={{ padding: "60px 100px 0" }}>
        <p style={{ fontSize: 14, letterSpacing: "0.32em", color: "#E07A5F", textTransform: "uppercase", fontWeight: 600, margin: 0 }}>
          Our mission
        </p>
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 110,
            lineHeight: 0.98,
            fontWeight: 500,
            margin: "20px 0 0",
            letterSpacing: "-0.01em",
            color: "#FDFBF7",
          }}
        >
          A Sangha for <span style={{ color: "#CFA052", fontStyle: "italic" }}>everyone.</span>
        </h1>
        <p
          style={{
            fontSize: 24,
            lineHeight: 1.5,
            color: "rgba(253,251,247,0.82)",
            margin: "36px 0 0",
            maxWidth: 980,
          }}
        >
          We are a <strong style={{ color: "#FDFBF7" }}>charitable public-benefit Buddhist network</strong>{" "}
          advancing Ambedkarite Buddhist teachings, education, meditation, equality, inclusion and
          community wellbeing — delivered through both in-person gatherings and our own{" "}
          <strong style={{ color: "#FDFBF7" }}>charitable digital platform</strong>, so that
          geography, mobility or means never bar anyone from the Dhamma.
        </p>
      </div>

      {/* Four pillars */}
      <div style={{ padding: "70px 100px 0" }}>
        <p style={{ fontSize: 14, letterSpacing: "0.32em", color: "#E07A5F", textTransform: "uppercase", fontWeight: 600, margin: 0 }}>
          What we do
        </p>
        <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <Pillar
            n="01"
            title="Educate"
            body="Free Dhamma teachings, structured online courses, and KS1–KS4 curriculum-linked workbooks for British schools. Buddhism, Ambedkarite thought, ethics and history made accessible for every age."
          />
          <Pillar
            n="02"
            title="Meditate"
            body="Weekly live meditation sessions and Dhamma talks, led by trained volunteer facilitators on our platform. Silence, breath, mettā — open to everyone, no prior experience needed."
          />
          <Pillar
            n="03"
            title="Empower"
            body="Community gatherings, festival celebrations (Buddha Purnima, Ambedkar Jayanti, Dhamma Chakra Pravartan Din), youth programmes and confidential mutual-aid. Education that liberates."
          />
          <Pillar
            n="04"
            title="Elevate"
            body="Promoting equality, social inclusion and community cohesion — particularly for disadvantaged or underserved communities — in the UK and internationally."
          />
        </div>
      </div>

      {/* Why we exist */}
      <div style={{ padding: "70px 100px 0" }}>
        <p style={{ fontSize: 14, letterSpacing: "0.32em", color: "#E07A5F", textTransform: "uppercase", fontWeight: 600, margin: 0 }}>
          Why we exist
        </p>
        <p style={{ fontSize: 22, lineHeight: 1.55, color: "rgba(253,251,247,0.85)", margin: "24px 0 0", maxWidth: 1040 }}>
          Most UK cities have no Ambedkarite Buddhist centre. Many in our community face barriers of
          distance, disability, caring responsibility, or the simple absence of a local Sangha. Our
          digital platform brings the Dhamma to <em style={{ color: "#CFA052" }}>every postcode</em> — and our in-person
          events bring the community physically together where we can.
        </p>
      </div>

      {/* Call to action band */}
      <div style={{ marginTop: 80, padding: "60px 100px", background: "linear-gradient(135deg, #001B3A 0%, #0B1F4D 100%)", borderTop: "1px solid rgba(207,160,82,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 60 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 14, letterSpacing: "0.32em", color: "#CFA052", textTransform: "uppercase", fontWeight: 600, margin: 0 }}>
              Join the Sangha
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 64, lineHeight: 1, fontWeight: 500, margin: "20px 0 0", color: "#FDFBF7" }}>
              Visit. Read. Sit. Belong.
            </h2>
            <p style={{ fontSize: 18, color: "rgba(253,251,247,0.7)", marginTop: 24, lineHeight: 1.6, maxWidth: 560 }}>
              Members, volunteers and visitors all welcome.<br />
              Free baseline membership · hardship waivers available · open to all faiths and none.
            </p>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, color: "#CFA052", marginTop: 30 }}>
              ambedkaritebuddhistglobalnetworkuk.com
            </p>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 58, fontStyle: "italic", color: "#CFA052", margin: 0, lineHeight: 1.1 }}>
              Educate<br />Meditate<br />Empower<br />Elevate
            </p>
            <p style={{ fontSize: 14, color: "rgba(253,251,247,0.5)", marginTop: 24, fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}>
              Rooted in Dhamma<br />Committed to humanity
            </p>
          </div>
        </div>
      </div>

      {/* Footer band */}
      <div style={{ padding: "30px 100px", background: "#001B3A", borderTop: "2px solid #CFA052", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: "rgba(253,251,247,0.6)" }}>
        <span>Charity registration in progress · CIO application in preparation</span>
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", color: "#CFA052" }}>Jai Bhim · Namo Buddhāya</span>
      </div>
    </div>
  );
}

function Pillar({ n, title, body }) {
  return (
    <div style={{ background: "rgba(253,251,247,0.04)", border: "1px solid rgba(207,160,82,0.25)", borderRadius: 4, padding: "32px 30px" }}>
      <div style={{ fontSize: 13, letterSpacing: "0.32em", color: "#CFA052", fontWeight: 600 }}>{n}</div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 44, color: "#FDFBF7", marginTop: 8, lineHeight: 1 }}>
        {title}
      </div>
      <p style={{ fontSize: 17, lineHeight: 1.55, color: "rgba(253,251,247,0.75)", marginTop: 18 }}>
        {body}
      </p>
    </div>
  );
}
